import type { ToolUseItem } from '../../../../contracts/harness.types';

import { isRecord } from '../../../../contracts/parse-utils';
import { readToolResultData } from '../../../../contracts/projects/harness/toolResult';
import type { AgentTodoItem, AgentTodoItemStatus, TodoState } from './types';

const TODO_STATUSES = new Set<AgentTodoItemStatus>([
  'pending',
  'in_progress',
  'completed',
  'cancelled',
]);

/** Unknown statuses are neutral, not a reason to drop the item. */
const parseStatus = (value: unknown): AgentTodoItemStatus =>
  typeof value === 'string' && TODO_STATUSES.has(value as AgentTodoItemStatus)
    ? (value as AgentTodoItemStatus)
    : 'pending';

const readTrimmed = (
  record: Record<string, unknown>,
  key: string
): string | undefined => {
  const raw = record[key];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
};

const readPlanTitle = (record: Record<string, unknown>): string | null =>
  readTrimmed(record, 'plan_title') ?? null;

export const parseTodoState = (value: unknown): TodoState | null => {
  if (!isRecord(value)) return null;
  if (!Array.isArray(value.items)) return null;

  const items: AgentTodoItem[] = [];
  for (const raw of value.items) {
    if (!isRecord(raw)) continue;
    const id = raw.id;
    const title = readTrimmed(raw, 'title');
    if (typeof id !== 'number' || !Number.isFinite(id) || !title) continue;
    const notes = readTrimmed(raw, 'notes');
    items.push({
      id,
      title,
      status: parseStatus(raw.status),
      ...(notes ? { notes } : {}),
    });
  }

  if (items.length === 0) return null;
  return { planTitle: readPlanTitle(value), items };
};

/**
 * `readToolResultData` already normalises the live SSE and snapshot
 * `provider_metadata` envelopes, including the `additional_metadata` unwrap.
 */
export const readTodoStateFromItem = (item: ToolUseItem): TodoState | null => {
  const data = readToolResultData(item);
  if (!data) return null;
  return parseTodoState(data.todo_state);
};

/**
 * Proposal-time input has no ids — the store assigns them at execution — so the
 * array index stands in and every item starts pending.
 */
export const parseProposedPlanInput = (
  input: Record<string, unknown>
): TodoState | null => {
  const rawItems = input.items;
  if (!Array.isArray(rawItems) || rawItems.length === 0) return null;

  const items: AgentTodoItem[] = [];
  for (const [index, raw] of rawItems.entries()) {
    if (!isRecord(raw)) return null;
    const title = readTrimmed(raw, 'title');
    if (!title) return null;
    const notes = readTrimmed(raw, 'notes');
    items.push({ id: index, title, status: 'pending', ...(notes ? { notes } : {}) });
  }

  return { planTitle: readPlanTitle(input), items };
};
